import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/addons/utils/SkeletonUtils.js";
import { modelUrl } from "./manifest.js";
import { batchStaticMeshes } from "./batch.js";

export interface LoadedModel {
  /** Template scene; call `instantiate()` for a scene-ready clone with its own mixer. */
  template: THREE.Group;
  clips: THREE.AnimationClip[];
  url: string;
}

export interface ModelInstance {
  object: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Record<string, THREE.AnimationAction>;
  play(clip: string, options?: { loop?: boolean; fade?: number; timeScale?: number }): THREE.AnimationAction | null;
}

const loader = new GLTFLoader();
const cache = new Map<string, Promise<LoadedModel>>();
let pendingModels = 0;

/** Includes image decoding and preparation, not merely completed HTTP responses. */
export function pendingModelCount(): number { return pendingModels; }

function prepare(gltf: GLTF): void {
  gltf.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const std = m as THREE.MeshStandardMaterial;
        if (std.emissive && std.emissiveIntensity > 1) std.toneMapped = true;
      }
    }
  });
  gltf.scene.userData["batchStats"] = batchStaticMeshes(gltf.scene, gltf.animations);
}

/** Loads (and caches) a glTF by asset id; buildings need the tier, zombies the variant. */
export function loadModel(id: string, variant?: number | string, lod: 0 | 1 = 0): Promise<LoadedModel> {
  const url = modelUrl(id, variant, lod);
  let p = cache.get(url);
  if (!p) {
    pendingModels++;
    p = new Promise<LoadedModel>((resolve, reject) => {
      loader.load(
        url,
        (gltf) => {
          try {
            prepare(gltf);
            resolve({ template: gltf.scene, clips: gltf.animations, url });
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        (err) => reject(err instanceof Error ? err : new Error(String(err))),
      );
    }).finally(() => { pendingModels--; });
    cache.set(url, p);
    void p.catch(() => {
      if (cache.get(url) === p) cache.delete(url);
    });
  }
  return p;
}

/** Deep-clones the template and wires an AnimationMixer with one action per clip. */
export function instantiate(model: LoadedModel): ModelInstance {
  const object = cloneSkeleton(model.template) as THREE.Group;
  const mixer = new THREE.AnimationMixer(object);
  const actions: Record<string, THREE.AnimationAction> = {};
  for (const clip of model.clips) actions[clip.name] = mixer.clipAction(clip);
  let current: THREE.AnimationAction | null = null;
  return {
    object,
    mixer,
    actions,
    play(clip, options = {}) {
      const action = actions[clip];
      if (!action) return null;
      const loop = options.loop ?? (clip === "idle" || clip === "walk");
      action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
      action.clampWhenFinished = !loop;
      action.timeScale = options.timeScale ?? 1;
      action.reset();
      if (current && current !== action) {
        current.crossFadeTo(action, options.fade ?? 0.15, false);
      }
      action.play();
      current = action;
      return action;
    },
  };
}

export function clearModelCache(): void {
  cache.clear();
}
